/**
 * Elasticity Heatmap Calculations
 * Client-side computation of multipliers and premium gain % for screener results
 * across multiple underlying % change scenarios using Black-Scholes.
 */

import { blackScholes } from './blackScholes';
import {
  getColorForGainPct,
  getMultiplierColor,
} from '../theme/heatmapPalette';

const RISK_FREE_RATE = 0.05;

/**
 * All available scenario percentages for column toggles
 */
export const ALL_SCENARIOS = [-50, -35, -20, -10, -5, 0, 5, 10, 15, 20, 35, 50, 100, 200, 1000];

/**
 * Default selected scenarios — focused 5-column set that captures the
 * most decision-relevant moves. Users can add more via the Heatmap Settings
 * sheet; their selection is persisted via getUserDefaultScenarios below.
 */
export const DEFAULT_SCENARIOS = new Set([-10, -5, 5, 10, 20]);

const SCENARIOS_STORAGE_KEY = 'screener_heatmap_user_scenarios';

/**
 * Read the user's persisted scenario set from localStorage, or fall back to
 * the shipped DEFAULT_SCENARIOS if nothing is saved (or the saved value is
 * malformed / empty).
 * @returns {Set<number>}
 */
export function getUserDefaultScenarios() {
  try {
    const raw = localStorage.getItem(SCENARIOS_STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_SCENARIOS);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return new Set(DEFAULT_SCENARIOS);
    return new Set(arr.map(Number).filter(n => !isNaN(n)));
  } catch {
    return new Set(DEFAULT_SCENARIOS);
  }
}

/**
 * Persist the user's scenario selection so it becomes their default on the
 * next run / page reload. No-op if localStorage is unavailable.
 * @param {Set<number>} set
 */
export function saveUserDefaultScenarios(set) {
  try {
    localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
}

/**
 * Clear the persisted scenario selection (used by the Heatmap Settings reset
 * action so that "Reset" goes back to the shipped DEFAULT_SCENARIOS).
 */
export function clearUserDefaultScenarios() {
  try {
    localStorage.removeItem(SCENARIOS_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Compute heatmap data for a single screener result row across all given scenarios.
 * @param {Object} row - Screener result row (must have Underlying Price, Strike, Days Until Expiration, Current Premium, Implied Volatility, Option Type, Position Type)
 * @param {number} daysToExit - Days until exit (from slider)
 * @param {number[]} scenarios - Array of scenario percentages (e.g., [-20, -10, 10, 20, ...])
 * @returns {Object} Map of scenarioPct -> { premiumGain, multiplier }
 */
/**
 * @param {Object} row - Screener result row
 * @param {number|null} daysToExit - Fixed days override, or null to use each row's own Days to Gain
 * @param {number[]} scenarios - Array of scenario percentages
 * @param {number|null} daysToExitPercent - Percentage of expiration (0-1), or null to ignore
 * @param {number|null} baselinePremium - Optional baseline override for premium gain %
 *   (used by the watchlist when the global baseline toggle is set to "avg_cost").
 *   Screener callers omit this and fall through to current premium.
 */
export function computeHeatmapRow(row, daysToExit, scenarios, daysToExitPercent = null, baselinePremium = null) {
  const underlyingPrice = parseFloat(row['Underlying Price']);
  const strike = parseFloat(row['Strike']);
  const daysUntilExp = parseFloat(row['Days Until Expiration']);
  const currentPremium = parseFloat(row['Current Premium']);
  const rawIV = parseFloat(row['Implied Volatility']);
  const optionType = row['Option Type']?.toLowerCase();
  const positionType = row['Position Type']?.toLowerCase();
  const isSell = positionType === 'sell';

  // Effective baseline for the premium-gain ratio. Falls back to current
  // premium when the override is missing or non-positive (toggle no-op).
  const effectiveBaseline = (Number.isFinite(baselinePremium) && baselinePremium > 0)
    ? baselinePremium
    : currentPremium;

  // Validate inputs
  if (!underlyingPrice || !strike || !daysUntilExp || !currentPremium || !optionType ||
      isNaN(underlyingPrice) || isNaN(strike) || isNaN(daysUntilExp) || isNaN(currentPremium)) {
    return {};
  }

  // Guard against very small premiums (avoids infinity)
  if (currentPremium < 0.01) {
    return {};
  }

  // IV: if > 5, assume it's in percentage form and convert to decimal
  const iv = rawIV > 5 ? rawIV / 100 : rawIV;
  if (!iv || isNaN(iv) || iv <= 0) {
    return {};
  }

  // Determine effective days to exit based on mode:
  // 1. Percentage of expiration (per-row), 2. Fixed override, 3. Per-contract fallback
  let effectiveDaysToExit;
  if (daysToExitPercent !== null && daysToExitPercent !== undefined) {
    effectiveDaysToExit = Math.max(1, Math.round(daysUntilExp * daysToExitPercent));
  } else if (daysToExit !== null && daysToExit !== undefined) {
    effectiveDaysToExit = daysToExit;
  } else {
    effectiveDaysToExit = parseFloat(row['Days to Gain']) || Math.max(1, Math.round(daysUntilExp * 0.35));
  }

  const timeToTarget = Math.max((daysUntilExp - effectiveDaysToExit) / 365, 0.0001);
  const result = {};

  for (const scenarioPct of scenarios) {
    const simulatedStock = underlyingPrice * (1 + scenarioPct / 100);

    // Ensure simulated stock is positive
    if (simulatedStock <= 0) {
      result[scenarioPct] = { premiumGain: null, multiplier: null, simulatedStock: null, projectedPremium: null };
      continue;
    }

    const projectedPremium = blackScholes(
      simulatedStock, strike, timeToTarget, RISK_FREE_RATE, iv, optionType
    );

    let premiumGain = 0;
    if (effectiveBaseline > 0) {
      if (isSell) {
        premiumGain = ((effectiveBaseline - projectedPremium) / effectiveBaseline) * 100;
      } else {
        premiumGain = ((projectedPremium - effectiveBaseline) / effectiveBaseline) * 100;
      }
    }

    // Multiplier = premium_gain / |scenario_pct|
    // Undefined for 0% scenario
    const multiplier = scenarioPct !== 0
      ? premiumGain / Math.abs(scenarioPct)
      : null;

    result[scenarioPct] = { premiumGain, multiplier, simulatedStock, projectedPremium };
  }

  return result;
}

// --- Heatmap coloring ---
// Palette logic lives in theme/heatmapPalette.js so the watchlist heatmap and
// the scoring builder share one source of truth. This module re-exports the
// legacy `getHeatmapCellColor` surface so existing consumers keep working.

/**
 * Get heatmap cell color based on display mode
 * @param {number} value - The cell value (multiplier or premium gain %)
 * @param {string} displayMode - 'multiplier' or 'premiumGain'
 * @returns {Object} Style object with backgroundColor (and optionally color)
 */
export function getHeatmapCellColor(value, displayMode) {
  if (displayMode === 'multiplier') {
    return getMultiplierColor(value);
  }
  return getColorForGainPct(value);
}

// --- Formatting ---

/**
 * Format multiplier value (e.g., 3.5x)
 * @param {number} value
 * @returns {string}
 */
export function formatMultiplier(value) {
  if (value == null || isNaN(value) || value <= 0) return '--';
  return `${Number(value).toFixed(1)}x`;
}

/**
 * Format premium gain percentage.
 * Rules: 6 digits max, no decimals if >= 1000, commas included.
 * Under 1000: show 1 decimal (e.g., 345.2%)
 * 1000+: no decimals, commas (e.g., 12,345%)
 * @param {number} value
 * @returns {string}
 */
export function formatPremiumGain(value) {
  if (value == null || isNaN(value)) return '--';
  const num = Number(value);
  const abs = Math.abs(num);

  if (abs < 1000) {
    return `${num.toFixed(1)}%`;
  }
  // 1000+: no decimals, add commas
  const formatted = Math.round(num).toLocaleString('en-US');
  return `${formatted}%`;
}

/**
 * Format a scenario column header label
 * @param {number} scenarioPct - The scenario percentage (e.g., -20, 10, 200)
 * @returns {string} Formatted label (e.g., "-20%", "+10%", "0%")
 */
export function formatScenarioLabel(scenarioPct) {
  if (scenarioPct === 0) return '0%';
  return scenarioPct > 0 ? `+${scenarioPct}%` : `${scenarioPct}%`;
}
